import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCommunications } from './hooks/useCommunications';
import {
  EmailTemplate,
  SMSTemplate,
  DeliveryLog,
  CreateEmailTemplateData,
  UpdateEmailTemplateData,
  CreateSMSTemplateData,
  UpdateSMSTemplateData,
} from './types';
import {
  CommunicationMetricsCards,
  EmailTemplatesTable,
  SMSTemplatesTable,
  DeliveryLogTable,
  DeliveryLogDetailModal,
  EmailTemplateModal,
  SMSTemplateModal,
} from './components';
import { PageLoader, FullWidthContainer, Button, Input, Select, Modal } from '../../components';

const Communications: React.FC = () => {
  const { t } = useTranslation();
  const {
    metrics,
    emailTemplates,
    smsTemplates,
    deliveryLogs,
    deliveryLogsPage,
    deliveryLogsPageSize,
    deliveryLogsTotalCount,
    deliveryLogsLoading,
    setDeliveryLogsPage,
    filters,
    setFilters,
    loading,
    error,
    createEmailTemplate,
    createSMSTemplate,
    updateEmailTemplate,
    updateSMSTemplate,
    deleteEmailTemplate,
    deleteSMSTemplate,
  } = useCommunications();

  const handleViewDeliveryLog = (id: string) => {
    const log = deliveryLogs.find(l => l.id === id);
    if (log) {
      setSelectedDeliveryLog(log);
      setIsDeliveryLogModalOpen(true);
    }
  };

  const [activeTab, setActiveTab] = useState<'templates' | 'delivery-log'>('templates');

  const [isEmailTemplateModalOpen, setIsEmailTemplateModalOpen] = useState(false);
  const [isSMSTemplateModalOpen, setIsSMSTemplateModalOpen] = useState(false);
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<EmailTemplate | null>(null);
  const [selectedSMSTemplate, setSelectedSMSTemplate] = useState<SMSTemplate | null>(null);

  const [deleteEmailModal, setDeleteEmailModal] = useState<{
    isOpen: boolean;
    template: EmailTemplate | null;
  }>({ isOpen: false, template: null });
  const [deleteSMSModal, setDeleteSMSModal] = useState<{
    isOpen: boolean;
    template: SMSTemplate | null;
  }>({ isOpen: false, template: null });

  const [selectedDeliveryLog, setSelectedDeliveryLog] = useState<DeliveryLog | null>(null);
  const [isDeliveryLogModalOpen, setIsDeliveryLogModalOpen] = useState(false);

  const handleEditEmailTemplate = (id: string) => {
    const template = emailTemplates.find(t => t.id === id);
    if (template) {
      setSelectedEmailTemplate(template);
      setIsEmailTemplateModalOpen(true);
    }
  };

  const handleDeleteEmailTemplate = (id: string) => {
    const template = emailTemplates.find(t => t.id === id);
    if (template) {
      setDeleteEmailModal({ isOpen: true, template });
    }
  };

  const handleEditSMSTemplate = (id: string) => {
    const template = smsTemplates.find(t => t.id === id);
    if (template) {
      setSelectedSMSTemplate(template);
      setIsSMSTemplateModalOpen(true);
    }
  };

  const handleDeleteSMSTemplate = (id: string) => {
    const template = smsTemplates.find(t => t.id === id);
    if (template) {
      setDeleteSMSModal({ isOpen: true, template });
    }
  };

  const handleCreateEmailTemplate = () => {
    setSelectedEmailTemplate(null);
    setIsEmailTemplateModalOpen(true);
  };

  const handleCreateSMSTemplate = () => {
    setSelectedSMSTemplate(null);
    setIsSMSTemplateModalOpen(true);
  };

  const handleEmailTemplateSubmit = async (
    data: CreateEmailTemplateData | UpdateEmailTemplateData
  ) => {
    if (selectedEmailTemplate) {
      await updateEmailTemplate(selectedEmailTemplate.id, data as UpdateEmailTemplateData);
    } else {
      await createEmailTemplate(data as CreateEmailTemplateData);
    }
    setIsEmailTemplateModalOpen(false);
    setSelectedEmailTemplate(null);
  };

  const handleSMSTemplateSubmit = async (data: CreateSMSTemplateData | UpdateSMSTemplateData) => {
    if (selectedSMSTemplate) {
      await updateSMSTemplate(selectedSMSTemplate.id, data as UpdateSMSTemplateData);
    } else {
      await createSMSTemplate(data as CreateSMSTemplateData);
    }
    setIsSMSTemplateModalOpen(false);
    setSelectedSMSTemplate(null);
  };

  const handleCloseEmailTemplateModal = () => {
    setIsEmailTemplateModalOpen(false);
    setSelectedEmailTemplate(null);
  };

  const handleCloseSMSTemplateModal = () => {
    setIsSMSTemplateModalOpen(false);
    setSelectedSMSTemplate(null);
  };

  const confirmDeleteEmailTemplate = async () => {
    if (deleteEmailModal.template) {
      await deleteEmailTemplate(deleteEmailModal.template.id);
      setDeleteEmailModal({ isOpen: false, template: null });
    }
  };

  const confirmDeleteSMSTemplate = async () => {
    if (deleteSMSModal.template) {
      await deleteSMSTemplate(deleteSMSModal.template.id);
      setDeleteSMSModal({ isOpen: false, template: null });
    }
  };

  const cancelDeleteEmailTemplate = () => {
    setDeleteEmailModal({ isOpen: false, template: null });
  };

  const cancelDeleteSMSTemplate = () => {
    setDeleteSMSModal({ isOpen: false, template: null });
  };

  const handleSearchChange = (value: string) => {
    setFilters({ ...filters, search: value });
  };

  const handleTypeChange = (value: string) => {
    setFilters({ ...filters, delivery_type: value as 'all' | 'email' | 'sms' });
  };

  const handleStatusChange = (value: string) => {
    setFilters({
      ...filters,
      status: value as 'all' | 'pending' | 'delivered' | 'failed' | 'cancelled',
    });
  };

  if (loading) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('communications.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('communications.subtitle')}</p>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="text-red-500 text-center">
              <p className="text-lg font-semibold">{t('common.error')}</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('communications.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">{t('communications.subtitle')}</p>
        </div>

        {/* Metrics */}
        <CommunicationMetricsCards metrics={metrics} />

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('templates')}
              className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'templates'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('communications.templates')}
            </button>
            <button
              onClick={() => setActiveTab('delivery-log')}
              className={`py-2 px-1 border-b-2 font-medium text-sm focus:outline-none ${
                activeTab === 'delivery-log'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {t('communications.deliveryLog')}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('communications.emailTemplates')}
              </h2>
              <Button
                variant="gradient-multi"
                onClick={handleCreateEmailTemplate}
                className="flex items-center gap-2"
              >
                <span className="material-icons">add</span>
                {t('communications.newEmailTemplate')}
              </Button>
            </div>
            <EmailTemplatesTable
              templates={emailTemplates}
              onEditTemplate={handleEditEmailTemplate}
              onDeleteTemplate={handleDeleteEmailTemplate}
              onCreateTemplate={handleCreateEmailTemplate}
            />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('communications.smsTemplates')}
              </h2>
              <Button
                variant="gradient-multi"
                onClick={handleCreateSMSTemplate}
                className="flex items-center gap-2"
              >
                <span className="material-icons">add</span>
                {t('communications.newSmsTemplate')}
              </Button>
            </div>
            <SMSTemplatesTable
              templates={smsTemplates}
              onEditTemplate={handleEditSMSTemplate}
              onDeleteTemplate={handleDeleteSMSTemplate}
              onCreateTemplate={handleCreateSMSTemplate}
            />
          </div>
        )}

        {activeTab === 'delivery-log' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('communications.communicationDeliveryLog')}
              </h2>
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="text"
                  placeholder={t('communications.searchLogs')}
                  value={filters.search}
                  onChange={e => handleSearchChange(e.target.value)}
                  className="w-full sm:w-80"
                />
                <Select
                  value={filters.delivery_type}
                  onChange={value => handleTypeChange(Array.isArray(value) ? value[0] : value)}
                  options={[
                    { value: 'all', label: 'All Types' },
                    { value: 'email', label: 'Email' },
                    { value: 'sms', label: 'SMS' },
                  ]}
                  className="w-full sm:w-40"
                />
                <Select
                  value={filters.status}
                  onChange={value => handleStatusChange(Array.isArray(value) ? value[0] : value)}
                  options={[
                    { value: 'all', label: 'All Status' },
                    { value: 'pending', label: 'Pending' },
                    { value: 'delivered', label: 'Delivered' },
                    { value: 'failed', label: 'Failed' },
                    { value: 'cancelled', label: 'Cancelled' },
                  ]}
                  className="w-full sm:w-40"
                />
              </div>
            </div>
            <DeliveryLogTable
              logs={deliveryLogs}
              onViewLog={handleViewDeliveryLog}
              page={deliveryLogsPage}
              pageSize={deliveryLogsPageSize}
              totalCount={deliveryLogsTotalCount}
              onPageChange={setDeliveryLogsPage}
              loading={deliveryLogsLoading}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      <EmailTemplateModal
        isOpen={isEmailTemplateModalOpen}
        onClose={handleCloseEmailTemplateModal}
        onSubmit={handleEmailTemplateSubmit}
        template={selectedEmailTemplate}
        loading={loading}
      />

      <SMSTemplateModal
        isOpen={isSMSTemplateModalOpen}
        onClose={handleCloseSMSTemplateModal}
        onSubmit={handleSMSTemplateSubmit}
        template={selectedSMSTemplate}
        loading={loading}
      />

      <DeliveryLogDetailModal
        isOpen={isDeliveryLogModalOpen}
        onClose={() => {
          setIsDeliveryLogModalOpen(false);
          setSelectedDeliveryLog(null);
        }}
        log={selectedDeliveryLog}
      />

      {/* Delete Email Template Modal */}
      <Modal
        isOpen={deleteEmailModal.isOpen}
        onClose={cancelDeleteEmailTemplate}
        title=""
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center space-y-6">
          {/* Warning Icon */}
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <span className="material-icons text-3xl text-red-600 dark:text-red-400">
              delete_forever
            </span>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('communications.deleteEmailTemplate')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('communications.areYouSureDeleteEmailTemplate')}{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                &ldquo;{deleteEmailModal.template?.title}&rdquo;
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('communications.thisActionCannotBeUndoneEmailTemplate')}
            </p>
          </div>

          {/* Template Info Card */}
          {deleteEmailModal.template && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                    email
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {deleteEmailModal.template.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {deleteEmailModal.template.subject} • {deleteEmailModal.template.status}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={cancelDeleteEmailTemplate}
              className="flex-1 px-6 py-3 font-medium"
            >
              {t('communications.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteEmailTemplate}
              className="flex-1 px-6 py-3 font-medium"
            >
              <span className="material-icons text-sm mr-2">delete</span>
              {t('communications.deleteTemplate')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete SMS Template Modal */}
      <Modal
        isOpen={deleteSMSModal.isOpen}
        onClose={cancelDeleteSMSTemplate}
        title=""
        size="sm"
        showCloseButton={false}
      >
        <div className="text-center space-y-6">
          {/* Warning Icon */}
          <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
            <span className="material-icons text-3xl text-red-600 dark:text-red-400">
              delete_forever
            </span>
          </div>

          {/* Content */}
          <div className="space-y-3">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('communications.deleteSMSTemplate')}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
              {t('communications.areYouSureDeleteSMSTemplate')}{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                &ldquo;{deleteSMSModal.template?.title}&rdquo;
              </span>
              ?
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('communications.thisActionCannotBeUndoneSMSTemplate')}
            </p>
          </div>

          {/* Template Info Card */}
          {deleteSMSModal.template && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                  <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                    sms
                  </span>
                </div>
                <div className="text-left">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {deleteSMSModal.template.title}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {deleteSMSModal.template.category} • {deleteSMSModal.template.status}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={cancelDeleteSMSTemplate}
              className="flex-1 px-6 py-3 font-medium"
            >
              {t('communications.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteSMSTemplate}
              className="flex-1 px-6 py-3 font-medium"
            >
              <span className="material-icons text-sm mr-2">delete</span>
              {t('communications.deleteTemplate')}
            </Button>
          </div>
        </div>
      </Modal>
    </FullWidthContainer>
  );
};

export { Communications };
